import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { deliveriesAPI } from "../lib/api";
import { formatDate, formatDateTime } from "../lib/dateUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { DatePicker } from "../components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Download,
  Filter,
  Search,
  DollarSign,
  Truck,
  Clock,
  MapPin,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Financials = () => {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [revenueRates, setRevenueRates] = useState({});
  const limit = 20;

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const canExport = isAdmin || isManager;

  // Calculate hours between check-in and check-out
  const calculateHours = (checkIn, checkOut) => {
    if (!checkIn?.timestamp || !checkOut?.timestamp) return null;
    const start = new Date(checkIn.timestamp);
    const end = new Date(checkOut.timestamp);
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours.toFixed(2);
  };

  // Calculate revenue based on distance and configured rates
  const calculateRevenue = (distanceKm) => {
    if (!distanceKm || distanceKm <= 0) return 0;
    
    let rate = 0;
    if (distanceKm <= 10) {
      rate = revenueRates["0-10"] || 0;
    } else if (distanceKm <= 15) {
      rate = revenueRates["10-15"] || 0;
    } else if (distanceKm <= 20) {
      rate = revenueRates["15-20"] || 0;
    } else if (distanceKm <= 25) {
      rate = revenueRates["20-25"] || 0;
    } else if (distanceKm <= 30) {
      rate = revenueRates["25-30"] || 0;
    } else {
      rate = revenueRates["30+"] || 0;
    }
    
    return parseFloat(rate) || 0;
  };

  // Format time from timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format date - using imported utility
  const formatDateLocal = (dateStr) => {
    if (!dateStr) return "-";
    return formatDate(dateStr);
  };

  const fetchDeliveries = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        status: "completed", // Only show completed deliveries in financials
      };

      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchTerm) params.search = searchTerm;

      const res = await deliveriesAPI.getAll(params);
      setDeliveries(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      toast.error("Failed to load financial data");
    } finally {
      setLoading(false);
    }
  }, [page, limit, dateFrom, dateTo, searchTerm]);

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRevenueRates(data.revenue_rates || {});
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, searchTerm]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem("token");
      
      let url = `${API_URL}/api/financials/export?`;
      if (dateFrom) url += `date_from=${dateFrom}&`;
      if (dateTo) url += `date_to=${dateTo}&`;
      if (searchTerm) url += `search=${searchTerm}&`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `financials_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Export completed");
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSearchTerm("");
  };

  const hasActiveFilters = dateFrom || dateTo || searchTerm;

  // Calculate summary stats
  const totalDistance = deliveries.reduce((sum, d) => sum + (d.distance_km || 0), 0);
  const totalHours = deliveries.reduce((sum, d) => {
    const hours = calculateHours(d.check_in, d.check_out);
    return sum + (hours ? parseFloat(hours) : 0);
  }, 0);
  const totalRevenue = deliveries.reduce((sum, d) => sum + calculateRevenue(d.distance_km), 0);

  if (loading && deliveries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="financials-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Financials
          </h1>
          <p className="text-slate-500">{total} completed deliveries</p>
        </div>
        {canExport && (
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider rounded-sm"
            data-testid="export-financials-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Export to Excel"}
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-sm">
              <Truck className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                Total Deliveries
              </p>
              <p className="text-2xl font-bold text-slate-900">{total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-sm">
              <MapPin className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                Total Distance
              </p>
              <p className="text-2xl font-bold text-slate-900">{totalDistance.toFixed(2)} KM</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-sm">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                Total Hours
              </p>
              <p className="text-2xl font-bold text-slate-900">{totalHours.toFixed(2)} hrs</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 border-green-200 bg-green-50/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-200 rounded-sm">
              <DollarSign className="w-6 h-6 text-green-700" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-green-700 font-semibold">
                Total Revenue
              </p>
              <p className="text-2xl font-bold text-green-800">${totalRevenue.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Search Bar */}
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by customer, driver, vehicle, location, excavator..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-sm"
                  data-testid="financials-search"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className={`rounded-sm ${hasActiveFilters ? "border-blue-500 bg-yellow-50" : ""}`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge className="ml-2 bg-blue-500 text-black rounded-none text-xs">
                    Active
                  </Badge>
                )}
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} className="rounded-sm text-slate-500">
                  Clear
                </Button>
              )}
            </div>

            {/* Expandable Filters */}
            {filtersExpanded && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Date From
                  </label>
                  <DatePicker
                    value={dateFrom}
                    onChange={(date) => setDateFrom(date)}
                    placeholder="From (dd/mm/yyyy)"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Date To
                  </label>
                  <DatePicker
                    value={dateTo}
                    onChange={(date) => setDateTo(date)}
                    placeholder="To (dd/mm/yyyy)"
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold uppercase text-xs">Date</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Check In</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Check Out</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Hours</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">DO #</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Customer</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Driver</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Vehicle</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Pickup</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Delivery</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Distance</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Excavator</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Type</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Billing</TableHead>
                  <TableHead className="font-semibold uppercase text-xs">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} className="text-center py-8 text-slate-500">
                      No completed deliveries found
                    </TableCell>
                  </TableRow>
                ) : (
                  deliveries.map((delivery) => {
                    const hours = calculateHours(delivery.check_in, delivery.check_out);
                    const revenue = calculateRevenue(delivery.distance_km);
                    return (
                      <TableRow key={delivery.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {formatDate(delivery.scheduled_date)}
                        </TableCell>
                        <TableCell>{formatTime(delivery.check_in?.timestamp)}</TableCell>
                        <TableCell>{formatTime(delivery.check_out?.timestamp)}</TableCell>
                        <TableCell>
                          {hours ? (
                            <Badge className="bg-amber-100 text-amber-800 rounded-none text-xs">
                              {hours} hrs
                            </Badge>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {delivery.delivery_order_number || delivery.check_out?.delivery_order_number || "-"}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate" title={delivery.customer_name}>
                          {delivery.customer_name}
                        </TableCell>
                        <TableCell className="max-w-[120px] truncate" title={delivery.assigned_driver_name}>
                          {delivery.assigned_driver_name || "-"}
                        </TableCell>
                        <TableCell>
                          {delivery.driver_vehicle_number || "-"}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate" title={delivery.pickup_location}>
                          {delivery.pickup_location || "-"}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate" title={delivery.delivery_location}>
                          {delivery.delivery_location || "-"}
                        </TableCell>
                        <TableCell>
                          {delivery.distance_km !== null && delivery.distance_km !== undefined ? (
                            <Badge className="bg-green-100 text-green-800 rounded-none text-xs">
                              {delivery.distance_km} KM
                            </Badge>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{delivery.excavator_model}</p>
                            <p className="text-slate-500 text-xs">{delivery.excavator_serial}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {delivery.delivery_type ? (
                            <Badge
                              className={`rounded-none text-xs ${
                                delivery.delivery_type === "Sales"
                                  ? "bg-green-100 text-green-800"
                                  : delivery.delivery_type === "Rental"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-slate-100 text-slate-800"
                              }`}
                            >
                              {delivery.delivery_type.toUpperCase()}
                            </Badge>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`rounded-none text-xs ${
                              delivery.billing_type === "chargeable"
                                ? "bg-green-100 text-green-800"
                                : delivery.billing_type === "partial"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-slate-100 text-slate-800"
                            }`}
                          >
                            {delivery.billing_type?.toUpperCase() || "-"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {revenue > 0 ? (
                            <span className="font-semibold text-green-700">${revenue.toFixed(2)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-sm"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded-sm"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Financials;
